import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Gauge, StatusBadge } from "@/components/Bits";
import api, { COUNTRY_NAMES, fmt, fmtTime } from "@/lib/api";
import { AlertTriangle, Activity, Send, Layers, ServerCog, RefreshCw } from "lucide-react";

const Kpi = ({ label, value, sub, alert, icon: Icon, testId }) => (
  <div
    data-testid={testId}
    className={`card-tactical p-5 ${alert ? "border-red-400" : ""}`}
  >
    <div className="flex items-start justify-between">
      <p className="text-sm text-gray-500">{label}</p>
      <Icon className={`h-4 w-4 ${alert ? "text-red-500" : "text-emerald-500"}`} />
    </div>
    <p className={`mono text-3xl font-semibold mt-3 ${alert ? "text-red-600" : "text-gray-900"}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  const load = async () => {
    const res = await api.get("/dashboard");
    setData(res.data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <Layout
      title="Tableau de bord"
      subtitle="Vue temps réel de la diffusion et de la santé des instances"
      actions={
        <button onClick={load} data-testid="refresh-dashboard-btn"
          className="rounded-lg border border-gray-200 bg-white p-2 hover:-translate-y-[1px] transition-transform duration-200">
          <RefreshCw className="h-4 w-4 text-gray-600" />
        </button>
      }
    >
      {!data ? (
        <p className="text-sm text-gray-500">Chargement…</p>
      ) : (
        <div className="space-y-6" data-testid="dashboard-page">
          {data.verification?.paused_reason && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4"
              data-testid="verification-alert">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-medium">Vérification WhatsApp en pause</p>
                <p className="text-xs mt-1">
                  {data.verification.paused_reason} — {data.verification.pending} contact(s) en
                  attente de vérification.
                </p>
              </div>
            </div>
          )}

          {data.handoff_count > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-red-300 bg-red-50 p-4"
              data-testid="handoff-banner">
              <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />
              <div className="text-sm text-red-900 min-w-0">
                <p className="font-medium">
                  {data.handoff_count} conversation(s) nécessitent votre intervention
                </p>
                <ul className="mt-2 space-y-1.5">
                  {data.handoff_alerts.map((a) => (
                    <li key={a.id} className="text-xs" data-testid={`handoff-item-${a.id}`}>
                      <span className="mono font-medium">{a.phone}</span>
                      {a.first_name ? ` · ${a.first_name}` : ""} — {a.reason}
                      <br />
                      <span className="opacity-80">« {a.last_message} »</span>
                      <Link to="/reponses" className="ml-2 underline"
                        data-testid={`handoff-open-${a.id}`}>Ouvrir la conversation</Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {data.alerts?.length > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="alert-banner">
              <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-medium">{data.alerts.length} instance(s) hors service</p>
                <p className="text-xs mt-1">
                  {data.alerts.map((a) => `${a.label} (${a.status})`).join(" · ")}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Kpi testId="kpi-sends-today" label="Envois aujourd'hui" value={fmt(data.sends_today)} icon={Send}
              sub={`${fmt(data.delivered_today)} livrés · ${fmt(data.read_today)} lus`} />
            <Kpi testId="kpi-queue" label="File d'attente" value={fmt(data.queue_size)} icon={Layers}
              sub={`${data.running_campaigns} en cours · ${data.scheduled_campaigns} planifiée(s)`} />
            <Kpi testId="kpi-optout" label="Taux de désinscription" value={`${data.optout_rate}%`} icon={Activity}
              alert={data.optout_alert} sub={data.optout_alert ? "Seuil d'alerte 2% dépassé" : "Sous le seuil de 2%"} />
            <Kpi testId="kpi-instances" label="Instances saines" value={`${data.healthy_instances}/${data.total_instances}`}
              icon={ServerCog} sub={`${fmt(data.total_contacts)} contacts · ${fmt(data.unread_replies)} réponse(s) non lue(s)`} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="card-tactical p-5 lg:col-span-2" data-testid="capacity-panel">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Capacité par instance</h2>
              {data.instances.length === 0 ? (
                <p className="text-sm text-gray-500">Aucune instance configurée.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {data.instances.map((i) => (
                    <div key={i.id} className="rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{i.label}</p>
                          <p className="text-xs text-gray-500">{COUNTRY_NAMES[i.country] || i.country} · Jour {i.warmup_day}</p>
                        </div>
                        <StatusBadge status={i.status} testId={`dash-inst-status-${i.id}`} />
                      </div>
                      <div className="space-y-3">
                        <Gauge value={i.sent_today} max={i.daily_cap} label="Quota jour" testId={`dash-gauge-day-${i.id}`} />
                        <Gauge value={i.sent_this_hour} max={i.hourly_cap} label="Quota heure" testId={`dash-gauge-hour-${i.id}`} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="card-tactical p-5" data-testid="live-feed">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Flux d'envoi</h2>
              <div className="space-y-3 max-h-[420px] overflow-y-auto">
                {data.feed.length === 0 && <p className="text-sm text-gray-500">Aucun envoi pour le moment.</p>}
                {data.feed.map((f) => (
                  <div key={f.id} className="flex items-start justify-between gap-3 border-b border-gray-100 pb-2.5">
                    <div className="min-w-0">
                      <p className="mono text-xs text-gray-900">{f.phone}</p>
                      <p className="text-[11px] text-gray-500 truncate">{f.instance_label} · {fmtTime(f.at)}</p>
                    </div>
                    <StatusBadge status={f.ok ? "sent" : "failed"} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="card-tactical p-5" data-testid="events-panel">
            <h2 className="text-base font-semibold text-gray-900 mb-3">Journal système</h2>
            {data.events.length === 0 ? (
              <p className="text-sm text-gray-500">Aucun évènement.</p>
            ) : (
              <ul className="space-y-2">
                {data.events.map((e) => (
                  <li key={e.id} className="flex items-center gap-3 text-sm text-gray-700">
                    <span className="mono text-[11px] text-gray-400 shrink-0">{fmtTime(e.created_at)}</span>
                    {e.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
